// EmargementCQP.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FileText, Download, Save, Calendar, Trash2, User, Check, Clock, ArrowDown, Search, X, FileDown } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

// Importer notre hook personnalisé
import useEmargement from '../../../hooks/useEmargement';

const EmargementCQP = () => {
  // Utiliser le hook pour accéder à toutes les fonctionnalités d'émargement
  const {
    apprentis,
    formateurs,
    emargements,
    sessions,
    loading,
    error,
    fetchData,
    saveEmargement,
    removeEmargement,
    generateAndUploadPdf,
    filterEmargements,
    clearError,
    uploadPdf,
    updateEmargementWithPdf
  } = useEmargement();
  
  // États locaux du composant
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedSession, setSelectedSession] = useState('CQP TAVA 2024/2026');
  const [securisationParcours, setSecurisationParcours] = useState(false);
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [viewPdfModal, setViewPdfModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentEmargementId, setCurrentEmargementId] = useState(null);
  const [apprentisAbsents, setApprentisAbsents] = useState([]); // Liste des IDs d'apprentis absents
  
  // Références
  const emargementFormRef = useRef(null);
  const signaturesRefs = useRef({});
  
  // ✅ Filtre pour ne garder que les formateurs autorisés (UNIQUEMENT ces 3)
  const FORMATEURS_AUTORISES = ['AUBRY', 'SCIAUVAUD', 'WEISS'];
  
  const formateursFiltres = React.useMemo(() => {
    if (!formateurs || formateurs.length === 0) return [];
    
    return formateurs.filter(formateur => {
      const nomUpper = formateur.nom.toUpperCase();
      return FORMATEURS_AUTORISES.some(nom => nomUpper === nom || nomUpper.includes(nom));
    });
  }, [formateurs]);
  
  // Debug logs pour voir les données chargées
  useEffect(() => {
    console.log("Apprentis chargés:", apprentis);
    console.log("Formateurs chargés:", formateurs);
    console.log("Formateurs filtrés:", formateursFiltres);
    console.log("Émargements chargés:", emargements);
  }, [apprentis, formateurs, formateursFiltres, emargements]);
  
  // Initialiser les références pour les signatures
  useEffect(() => {
    console.log("Initialisation des références pour signatures avec", apprentis?.length || 0, "apprentis et", formateursFiltres?.length || 0, "formateurs");
    
    signaturesRefs.current = {};
    
    if (apprentis && apprentis.length > 0) {
      apprentis.forEach(apprenti => {
        signaturesRefs.current[`apprenti_${apprenti.id}_matin`] = React.createRef();
        signaturesRefs.current[`apprenti_${apprenti.id}_apresmidi`] = React.createRef();
      });
    }
    
    if (formateursFiltres && formateursFiltres.length > 0) {
      formateursFiltres.forEach(formateur => {
        signaturesRefs.current[`formateur_${formateur.id}_matin`] = React.createRef();
        signaturesRefs.current[`formateur_${formateur.id}_apresmidi`] = React.createRef();
      });
    }
  }, [apprentis, formateursFiltres]);
  
  // Rafraîchir les données au démarrage
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Gérer l'état d'absence d'un apprenti
  const toggleAbsence = (apprentiId) => {
    setApprentisAbsents(prev => {
      if (prev.includes(apprentiId)) {
        return prev.filter(id => id !== apprentiId);
      } else {
        return [...prev, apprentiId];
      }
    });
    
    // Effacer les signatures si l'apprenti est marqué absent
    clearSignature(`apprenti_${apprentiId}_matin`);
    clearSignature(`apprenti_${apprentiId}_apresmidi`);
  };
  
  // Effacer une signature spécifique
  const clearSignature = (key) => {
    if (signaturesRefs.current[key] && signaturesRefs.current[key].current) {
      signaturesRefs.current[key].current.clear();
    }
  };
  
  // Effacer toutes les signatures
  const clearAllSignatures = () => {
    Object.keys(signaturesRefs.current).forEach(key => {
      if (signaturesRefs.current[key] && signaturesRefs.current[key].current) {
        signaturesRefs.current[key].current.clear();
      }
    });
  };
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setShowCreationForm(false);
    setSecurisationParcours(false);
    setApprentisAbsents([]);
    clearAllSignatures();
  };
  
  // Vérifier quels formateurs ont signé
  const getFormateursSignes = (signatures) => {
    const formateursSignes = [];
    
    Object.keys(signatures || {}).forEach(key => {
      if (key.startsWith('formateur_') && signatures[key]) {
        const formateurId = key.split('_')[1];
        if (!formateursSignes.includes(formateurId)) {
          formateursSignes.push(formateurId);
        }
      }
    });
    
    return formateursSignes;
  };
  
  // Sauvegarder un émargement avec ses signatures
  const handleSaveEmargement = async () => {
    try {
      setSaving(true);
      
      // Collecter toutes les signatures
      const signatures = {};
      let hasAtLeastOneSignature = false;
      
      for (const key in signaturesRefs.current) {
        // Ne pas collecter les signatures des apprentis absents
        if (key.startsWith('apprenti_')) {
          const apprentiId = key.split('_')[1];
          if (apprentisAbsents.includes(apprentiId)) {
            continue;
          }
        }
        
        if (signaturesRefs.current[key] && signaturesRefs.current[key].current) {
          const isEmpty = signaturesRefs.current[key].current.isEmpty();
          signatures[key] = isEmpty ? null : signaturesRefs.current[key].current.toDataURL('image/png');
          
          if (!isEmpty) {
            hasAtLeastOneSignature = true;
          }
        }
      }
      
      if (!hasAtLeastOneSignature && apprentisAbsents.length === 0) {
        alert('Veuillez ajouter au moins une signature ou marquer des apprentis absents avant d\'enregistrer.');
        setSaving(false);
        return;
      }
      
      // Déterminer les formateurs qui ont signé
      const formateursSignes = getFormateursSignes(signatures);
      
      // Créer l'objet d'émargement
      const emargementData = {
        date: selectedDate,
        session: selectedSession,
        signatures: signatures,
        securisationParcours: securisationParcours,
        apprentisIds: apprentis ? apprentis.map(a => a.id) : [],
        apprentisAbsentsIds: apprentisAbsents,
        formateursSignes: formateursSignes // Uniquement les formateurs qui ont signé
      };
      
      // Sauvegarder l'émargement en utilisant la fonction du hook
      const newEmargement = await saveEmargement(emargementData);
      setCurrentEmargementId(newEmargement.id);
      
      // Générer et uploader le PDF
      setGenerating(true);
      
      try {
        await generatePDF(newEmargement.id);
        
        // Réinitialiser le formulaire
        resetForm();
        
        alert('Feuille d\'émargement enregistrée avec succès!');
      } catch (pdfError) {
        console.error('Erreur lors de la génération du PDF:', pdfError);
        alert('Émargement enregistré mais erreur lors de la génération du PDF: ' + pdfError.message);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde: ' + error.message);
    } finally {
      setSaving(false);
      setGenerating(false);
    }
  };
  
  // Générer un PDF à partir du formulaire
  const generatePDF = async (emargementId) => {
    try {
      setGenerating(true);
      
      // Importer de façon dynamique (pour éviter les problèmes côté serveur)
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      
      // Options pour la capture du formulaire
      const options = {
        backgroundColor: '#1e293b',
        scale: 2,
        useCORS: true,
        logging: true,
      };
      
      if (!emargementFormRef.current) {
        throw new Error("Impossible de générer le PDF: la référence au formulaire est nulle");
      }
      
      // Capture le formulaire en image
      const canvas = await html2canvas(emargementFormRef.current, options);
      const imgData = canvas.toDataURL('image/png');
      
      // Crée un nouveau document PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Dimensions du PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Ajoute l'image au PDF
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // Génère le PDF sous forme de blob
      const pdfBlob = pdf.output('blob');
      
      // Nom du fichier
      const fileName = `emargement_${selectedSession.replace(/\//g, '-')}_${selectedDate}.pdf`;
      
      // Crée un objet File à partir du Blob
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      // Télécharge le fichier sur Supabase Storage
      const filePath = `${emargementId}/${fileName}`;
      const pdfUrl = await uploadPdf(pdfFile, filePath);
      
      // Met à jour l'enregistrement dans Supabase avec l'URL du PDF
      await updateEmargementWithPdf(emargementId, pdfUrl);
      
      // Rend l'URL disponible pour l'affichage
      setViewPdfUrl(pdfUrl);
      setViewPdfModal(true);
      
      return pdfUrl;
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      throw error;
    } finally {
      setGenerating(false);
    }
  };
  
  // Générer un PDF pour un émargement existant
  const regeneratePDF = async (emargement) => {
    try {
      setGenerating(true);
      setCurrentEmargementId(emargement.id);
      
      // Importer html2canvas et jsPDF dynamiquement
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      
      // Créer un élément temporaire avec les données de l'émargement
      const tempDiv = document.createElement('div');
      tempDiv.className = "bg-slate-800 p-6 rounded-lg";
      
      // ✅ Filtrer les formateurs pour le PDF
      const formateursFiltresToDisplay = formateursFiltres.filter(f => emargement.formateurs_ids.includes(f.id));
      
      // Ajouter le contenu HTML pour le PDF
      tempDiv.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif; background-color: #1e293b; color: white;">
          <div style="text-align: center; margin-bottom: 20px;">
            <h1 style="font-size: 24px; margin-bottom: 10px;">FEUILLE D'ÉMARGEMENT</h1>
            <div style="color: #93c5fd; font-size: 18px; margin-bottom: 5px;">${emargement.session}</div>
            <div>${formatDateToHuman(emargement.date)}</div>
          </div>
          
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: rgba(51, 65, 85, 0.5);">
                <th style="padding: 10px; text-align: left; border: 1px solid #4b5563; color: #93c5fd; width: 30%;">Nom et Prénom</th>
                <th style="padding: 10px; text-align: center; border: 1px solid #4b5563; color: #93c5fd; width: 35%;">Signature Matin<br><span style="font-size: 12px; color: #94a3b8;">8h00 - 12h00</span></th>
                <th style="padding: 10px; text-align: center; border: 1px solid #4b5563; color: #93c5fd; width: 35%;">Signature Après-midi<br><span style="font-size: 12px; color: #94a3b8;">13h00 - 17h00</span></th>
              </tr>
            </thead>
            <tbody>
              <!-- Section Apprentis -->
              <tr style="background-color: rgba(147, 51, 234, 0.1);">
                <td colspan="3" style="padding: 8px; color: #d8b4fe; font-weight: 500;">Apprentis</td>
              </tr>
              ${apprentis
                .filter(a => emargement.apprentis_ids.includes(a.id))
                .map(apprenti => {
                  const isAbsent = emargement.apprentis_absents_ids && emargement.apprentis_absents_ids.includes(apprenti.id);
                  
                  return `
                    <tr style="border-bottom: 1px solid rgba(75, 85, 99, 0.3);">
                      <td style="padding: 10px; color: white;">${apprenti.nom} ${apprenti.prenom}</td>
                      <td style="padding: 10px; text-align: center;">
                        ${isAbsent 
                          ? '<div style="color: #f87171; font-weight: 500;">ABSENT</div>' 
                          : emargement.signatures[`apprenti_${apprenti.id}_matin`] 
                            ? `<img src="${emargement.signatures[`apprenti_${apprenti.id}_matin`]}" style="max-height: 60px; max-width: 100%; display: block; margin: 0 auto;" />`
                            : ''}
                      </td>
                      <td style="padding: 10px; text-align: center;">
                        ${isAbsent 
                          ? '<div style="color: #f87171; font-weight: 500;">ABSENT</div>' 
                          : emargement.signatures[`apprenti_${apprenti.id}_apresmidi`] 
                            ? `<img src="${emargement.signatures[`apprenti_${apprenti.id}_apresmidi`]}" style="max-height: 60px; max-width: 100%; display: block; margin: 0 auto;" />`
                            : ''}
                      </td>
                    </tr>
                  `;
                }).join('')}
              
              <!-- Section Formateurs -->
              <tr style="background-color: rgba(59, 130, 246, 0.1);">
                <td colspan="3" style="padding: 8px; color: #93c5fd; font-weight: 500;">Formateurs</td>
              </tr>
              ${formateursFiltresToDisplay
                .map(formateur => `
                  <tr style="border-bottom: 1px solid rgba(75, 85, 99, 0.3);">
                    <td style="padding: 10px; color: white;">${formateur.nom} ${formateur.prenom}</td>
                    <td style="padding: 10px; text-align: center;">
                      ${emargement.signatures[`formateur_${formateur.id}_matin`] 
                        ? `<img src="${emargement.signatures[`formateur_${formateur.id}_matin`]}" style="max-height: 60px; max-width: 100%; display: block; margin: 0 auto;" />`
                        : ''}
                    </td>
                    <td style="padding: 10px; text-align: center;">
                      ${emargement.signatures[`formateur_${formateur.id}_apresmidi`] 
                        ? `<img src="${emargement.signatures[`formateur_${formateur.id}_apresmidi`]}" style="max-height: 60px; max-width: 100%; display: block; margin: 0 auto;" />`
                        : ''}
                    </td>
                  </tr>
                `).join('')}
            </tbody>
          </table>
          
          ${emargement.securisation_parcours 
            ? '<div style="padding: 10px; background-color: rgba(22, 163, 74, 0.1); border: 1px solid #15803d; color: #4ade80; border-radius: 4px; text-align: center; margin-top: 10px;">Sécurisation du parcours mise en place pour cette session</div>' 
            : ''}
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      // Générer le canvas à partir de l'élément temporaire
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: '#1e293b',
        scale: 2,
        useCORS: true,
        logging: true,
      });
      
      // Supprimer l'élément temporaire
      document.body.removeChild(tempDiv);
      
      const imgData = canvas.toDataURL('image/png');
      
      // Créer le PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Ajouter l'image au PDF
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * ratio, imgHeight * ratio);
      
      // Nom du fichier
      const fileName = `emargement_${emargement.session.replace(/\//g, '-')}_${emargement.date}.pdf`;
      
      // Convertir en blob et file
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      // Télécharger sur Supabase
      const filePath = `${emargement.id}/${fileName}`;
      const pdfUrl = await uploadPdf(pdfFile, filePath);
      
      // Mettre à jour l'émargement avec l'URL du PDF
      await updateEmargementWithPdf(emargement.id, pdfUrl);
      
      // Afficher le PDF
      setViewPdfUrl(pdfUrl);
      setViewPdfModal(true);
      
      return pdfUrl;
    } catch (error) {
      console.error('Erreur lors de la régénération du PDF:', error);
      alert('Erreur lors de la génération du PDF: ' + error.message);
      throw error;
    } finally {
      setGenerating(false);
    }
  };
  
  // Supprimer un émargement
  const handleDeleteEmargement = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette feuille d\'émargement?')) {
      return;
    }
    
    try {
      await removeEmargement(id);
      alert('Feuille d\'émargement supprimée avec succès!');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression: ' + error.message);
    }
  };
  
  // Télécharger un PDF
  const handleDownloadPdf = (pdfUrl, filename) => {
    if (!pdfUrl) {
      alert('Aucun PDF disponible pour cet émargement.');
      return;
    }
    
    // Créer un lien temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename || 'emargement.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Format de date lisible
  const formatDateToHuman = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd MMMM yyyy', { locale: fr });
  };
  
  // Filtrer les émargements selon la recherche
  const filteredEmargements = searchTerm && emargements ? 
    emargements.filter(e => 
      e.session?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.date?.includes(searchTerm)
    ) : 
    emargements;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40"
    >
      <div className="container mx-auto px-4 mb-32">
        {/* Titre et description */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8, 
            ease: [0.4, 0, 0.2, 1] 
          }}
          className="text-center mb-10"
        >
          <motion.h1
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-6xl font-bold text-center mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"
          >
            Émargement CQP
          </motion.h1>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-xl text-center text-white font-medium max-w-2xl mx-auto mb-12 bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm"
          >
            Gestion des feuilles d'émargement pour les formations CQP
          </motion.p>
        </motion.div>

        {/* Boutons d'action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-4 mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreationForm(!showCreationForm)}
            className="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            {showCreationForm ? (
              <>
                <X className="w-5 h-5" />
                <span>Annuler la création</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Nouvelle feuille d'émargement</span>
              </>
            )}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchData}
            className="px-6 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <ArrowDown className="w-5 h-5" />
            <span>Actualiser la liste</span>
          </motion.button>
        </motion.div>

        {/* Formulaire de création */}
        {showCreationForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-slate-800/60 rounded-lg backdrop-blur-sm p-6 mb-8 overflow-hidden"
            ref={emargementFormRef}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
              <FileText className="w-6 h-6 mr-3 text-blue-400" />
              Nouvelle feuille d'émargement
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Sélection de date */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300">
                  Date de la session
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                  />
                  <Calendar className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                </div>
              </div>
              
              {/* Sélection de la session CQP */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300">
                  Session CQP
                </label>
                <select
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  {sessions && sessions.length > 0 ? (
                    sessions.map(session => (
                      <option key={session} value={session}>
                        {session}
                      </option>
                    ))
                  ) : (
                    <option value="CQP TAVA 2024/2026">CQP TAVA 2024/2026</option>
                  )}
                </select>
              </div>
            </div>
            
            {/* En-tête du document */}
            <div className="mb-8 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">FEUILLE D'ÉMARGEMENT</h3>
                <div className="text-lg text-blue-300 mb-1">{selectedSession}</div>
                <div className="text-md text-white">{formatDateToHuman(selectedDate)}</div>
              </div>
            </div>
            
            {/* Tableau d'émargement */}
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-slate-700/50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 border-b border-slate-600 rounded-tl-lg" style={{ width: '25%' }}>
                      Nom et Prénom
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 border-b border-slate-600 text-center" style={{ width: '30%' }}>
                      <div className="flex flex-col items-center">
                        <span>Signature Matin</span>
                        <span className="text-xs text-slate-400">8h00 - 12h00</span>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 border-b border-slate-600 text-center" style={{ width: '30%' }}>
                      <div className="flex flex-col items-center">
                        <span>Signature Après-midi</span>
                        <span className="text-xs text-slate-400">13h00 - 17h00</span>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 border-b border-slate-600 text-center rounded-tr-lg" style={{ width: '15%' }}>
                      Absence
                    </th>
                  </tr>
                </thead>
                
                <tbody>
                  {/* Apprentis */}
                  {apprentis && apprentis.length > 0 ? (
                    <>
                      <tr className="bg-slate-700/20">
                        <td colSpan="4" className="px-4 py-2 text-purple-300 font-medium">
                          Apprentis
                        </td>
                      </tr>
                      
                      {apprentis.map(apprenti => (
                        <tr key={apprenti.id} className="border-b border-slate-700/50">
                          <td className="px-4 py-3 text-white">
                            {apprenti.nom} {apprenti.prenom}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative w-full" style={{ 
                              height: '110px', 
                              border: '1px solid #4B5563', 
                              borderRadius: '0.5rem', 
                              background: apprentisAbsents.includes(apprenti.id) ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)' 
                            }}>
                              {apprentisAbsents.includes(apprenti.id) ? (
                                <div className="flex items-center justify-center h-full text-red-400 font-medium">
                                  ABSENT
                                </div>
                              ) : (
                                <>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <SignatureCanvas
                                      ref={signaturesRefs.current[`apprenti_${apprenti.id}_matin`]}
                                      canvasProps={{
                                        width: 280,
                                        height: 90,
                                        className: 'signature-canvas'
                                      }}
                                      backgroundColor="transparent"
                                      penColor="white"
                                    />
                                  </div>
                                  <button 
                                    onClick={() => clearSignature(`apprenti_${apprenti.id}_matin`)}
                                    className="absolute bottom-1 right-2 text-xs text-slate-400 hover:text-red-400 bg-slate-800/50 px-2 py-1 rounded"
                                  >
                                    Effacer
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative w-full" style={{ 
                              height: '110px', 
                              border: '1px solid #4B5563', 
                              borderRadius: '0.5rem', 
                              background: apprentisAbsents.includes(apprenti.id) ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)' 
                            }}>
                              {apprentisAbsents.includes(apprenti.id) ? (
                                <div className="flex items-center justify-center h-full text-red-400 font-medium">
                                  ABSENT
                                </div>
                              ) : (
                                <>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <SignatureCanvas
                                      ref={signaturesRefs.current[`apprenti_${apprenti.id}_apresmidi`]}
                                      canvasProps={{
                                        width: 280,
                                        height: 90,
                                        className: 'signature-canvas'
                                      }}
                                      backgroundColor="transparent"
                                      penColor="white"
                                    />
                                  </div>
                                  <button 
                                    onClick={() => clearSignature(`apprenti_${apprenti.id}_apresmidi`)}
                                    className="absolute bottom-1 right-2 text-xs text-slate-400 hover:text-red-400 bg-slate-800/50 px-2 py-1 rounded"
                                  >
                                    Effacer
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-5 h-5 text-red-600 bg-slate-700 border-slate-600 rounded focus:ring-red-500"
                                checked={apprentisAbsents.includes(apprenti.id)}
                                onChange={() => toggleAbsence(apprenti.id)}
                              />
                              <span className="ml-2 text-white">Absent</span>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-4 text-slate-400 text-center">
                        Aucun apprenti disponible. Veuillez ajouter des apprentis dans la base de données.
                      </td>
                    </tr>
                  )}
                  
                  {/* Formateurs (filtrés) */}
                  {formateursFiltres && formateursFiltres.length > 0 ? (
                    <>
                      <tr className="bg-slate-700/20">
                        <td colSpan="4" className="px-4 py-2 text-blue-300 font-medium">
                          Formateurs
                        </td>
                      </tr>
                      
                      {formateursFiltres.map(formateur => (
                        <tr key={formateur.id} className="border-b border-slate-700/50">
                          <td className="px-4 py-3 text-white">
                            {formateur.nom} {formateur.prenom}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative w-full" style={{ height: '110px', border: '1px solid #4B5563', borderRadius: '0.5rem', background: 'rgba(255, 255, 255, 0.05)' }}>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <SignatureCanvas
                                  ref={signaturesRefs.current[`formateur_${formateur.id}_matin`]}
                                  canvasProps={{
                                    width: 280,
                                    height: 90,
                                    className: 'signature-canvas'
                                  }}
                                  backgroundColor="transparent"
                                  penColor="white"
                                />
                              </div>
                              <button 
                                onClick={() => clearSignature(`formateur_${formateur.id}_matin`)}
                                className="absolute bottom-1 right-2 text-xs text-slate-400 hover:text-red-400 bg-slate-800/50 px-2 py-1 rounded"
                              >
                                Effacer
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative w-full" style={{ height: '110px', border: '1px solid #4B5563', borderRadius: '0.5rem', background: 'rgba(255, 255, 255, 0.05)' }}>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <SignatureCanvas
                                  ref={signaturesRefs.current[`formateur_${formateur.id}_apresmidi`]}
                                  canvasProps={{
                                    width: 280,
                                    height: 90,
                                    className: 'signature-canvas'
                                  }}
                                  backgroundColor="transparent"
                                  penColor="white"
                                />
                              </div>
                              <button 
                                onClick={() => clearSignature(`formateur_${formateur.id}_apresmidi`)}
                                className="absolute bottom-1 right-2 text-xs text-slate-400 hover:text-red-400 bg-slate-800/50 px-2 py-1 rounded"
                              >
                                Effacer
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-slate-400 text-sm">Formateur</span>
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-4 text-slate-400 text-center">
                        Aucun formateur autorisé disponible (AUBRY, SCIAUVAUD, WEISS).
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Sécurisation du parcours */}
            <div className="mb-8">
              <label className="flex items-center space-x-3 text-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={securisationParcours}
                  onChange={(e) => setSecurisationParcours(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-slate-600 rounded focus:ring-blue-500 focus:ring-opacity-25 bg-slate-700"
                />
                <span>Sécurisation du parcours</span>
              </label>
              <p className="text-sm text-slate-400 mt-1 ml-8">
                Cochez cette case si des mesures de sécurisation du parcours ont été mises en place pour cette session.
              </p>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetForm}
                className="px-6 py-3 border border-slate-600 text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                Annuler
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearAllSignatures}
                className="px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <X className="w-5 h-5" />
                <span>Effacer toutes les signatures</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveEmargement}
                disabled={saving || generating || (!apprentis?.length && !formateursFiltres?.length)}
                className={`px-6 py-3 ${(!apprentis?.length && !formateursFiltres?.length) ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg transition-colors flex items-center space-x-2`}
              >
                {saving || generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>{generating ? "Génération PDF..." : "Enregistrement..."}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Enregistrer et générer PDF</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Liste des émargements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/60 rounded-lg backdrop-blur-sm p-6 mb-8"
        >
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
            <Clock className="w-6 h-6 mr-3 text-blue-400" />
            Historique des émargements
          </h2>
          
          {/* Recherche */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher par session ou date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              />
              <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-3.5 text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Affichage des émargements selon l'état */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-500/20 border border-red-500 text-white p-4 rounded-lg">
              <p>{error}</p>
              <button 
                onClick={() => {
                  clearError();
                  fetchData();
                }}
                className="mt-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm"
              >
                Réessayer
              </button>
            </div>
          ) : !emargements || emargements.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Aucune feuille d'émargement trouvée</p>
              <p className="mt-2">Commencez par créer une nouvelle feuille d'émargement</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 rounded-tl-lg">Date</th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300">Session</th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 text-center">Formateurs</th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 text-center">Apprentis</th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 text-center">Sécurisation</th>
                    <th className="px-4 py-3 text-sm font-medium text-blue-300 rounded-tr-lg">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {filteredEmargements && filteredEmargements.map(emargement => (
                    <tr key={emargement.id} className="hover:bg-slate-700/30">
                      <td className="px-4 py-4">{formatDateToHuman(emargement.date)}</td>
                      <td className="px-4 py-4">{emargement.session}</td>
                      <td className="px-4 py-4 text-center">
                        {emargement.formateurs_ids?.length || 0}
                        {emargement.formateurs_ids?.length > 0 && formateursFiltres?.length > 0 && (
                          <div className="text-xs text-blue-300 mt-1">
                            {formateursFiltres
                              .filter(f => emargement.formateurs_ids.includes(f.id))
                              .map(f => `${f.nom.split(' ')[0]} ${f.prenom.charAt(0)}.`)
                              .join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {emargement.apprentis_ids?.length - (emargement.apprentis_absents_ids?.length || 0) || 0}
                        {emargement.apprentis_absents_ids?.length > 0 && (
                          <div className="text-xs text-red-300 mt-1">
                            ({emargement.apprentis_absents_ids.length} absent{emargement.apprentis_absents_ids.length > 1 ? 's' : ''})
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {emargement.securisation_parcours ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                            <Check className="w-3 h-3 mr-1" />
                            Oui
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-500/20 text-slate-300">
                            <X className="w-3 h-3 mr-1" />
                            Non
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex space-x-2">
                          {emargement.pdf_url ? (
                            <>
                              <a 
                                href={emargement.pdf_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                                title="Ouvrir le PDF"
                              >
                                <Search className="w-4 h-4 text-blue-400" />
                              </a>
                              <button 
                                onClick={() => handleDownloadPdf(
                                  emargement.pdf_url, 
                                  `emargement_${emargement.session.replace(/\//g, '-')}_${emargement.date}.pdf`
                                )}
                                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                                title="Télécharger le PDF"
                              >
                                <Download className="w-4 h-4 text-green-400" />
                              </button>
                            </>
                          ) : (
                            <button 
                              onClick={() => {
                                setGenerating(true);
                                setCurrentEmargementId(emargement.id);
                                regeneratePDF(emargement)
                                  .then(pdfUrl => {
                                    setViewPdfUrl(pdfUrl);
                                    setViewPdfModal(true);
                                    setGenerating(false);
                                  })
                                  .catch(err => {
                                    console.error(err);
                                    alert('Erreur lors de la génération du PDF');
                                    setGenerating(false);
                                  });
                              }}
                              disabled={generating}
                              className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                              title="Générer le PDF"
                            >
                              {generating && currentEmargementId === emargement.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                              ) : (
                                <FileDown className="w-4 h-4 text-orange-400" />
                              )}
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteEmargement(emargement.id)}
                            className="p-2 bg-slate-700 hover:bg-red-700 rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Modal d'affichage du PDF */}
        {viewPdfModal && viewPdfUrl && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-slate-700">
                <h3 className="text-xl font-semibold text-white">Aperçu du PDF</h3>
                <button 
                  onClick={() => setViewPdfModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-full"
                >
                  <X className="w-5 h-5 text-slate-300" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-4">
                <iframe 
                  src={viewPdfUrl} 
                  className="w-full h-full rounded border border-slate-700" 
                  title="Aperçu du PDF d'émargement"
                ></iframe>
              </div>
              <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => handleDownloadPdf(viewPdfUrl)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Télécharger</span>
                </button>
                <button
                  onClick={() => setViewPdfModal(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Style pour les signature pads */}
        <style jsx global>{`
          .signature-canvas {
            cursor: crosshair;
            touch-action: none;
          }
        `}</style>
      </div>
    </motion.div>
  );
};

export default EmargementCQP;